import React, { useState, useEffect } from 'react';
import './FacebookLeadsTab.css';

// API base URL
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL = rawApiUrl.startsWith('http://') || rawApiUrl.startsWith('https://') 
  ? rawApiUrl 
  : `https://${rawApiUrl}`;

function FacebookLeadsTab() {
  const [searchKeywords, setSearchKeywords] = useState('');
  const [region, setRegion] = useState('IL');
  const [adType, setAdType] = useState('ALL');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [leadsError, setLeadsError] = useState(null);
  
  const [extracting, setExtracting] = useState({});
  const [, setSaving] = useState({});

  // Load saved leads on mount
  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoadingLeads(true);
    setLeadsError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/facebook-ads/leads`);
      const data = await response.json();
      
      if (data.success) {
        setLeads(data.leads || []);
      } else {
        setLeadsError(data.error || 'Failed to load leads');
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      setLeadsError('Failed to load leads: ' + error.message);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchKeywords.trim()) {
      setSearchError('Please enter search keywords');
      return;
    }
    
    setSearching(true);
    setSearchError(null);
    setSearchResults([]);
    
    try {
      // Use browser-based search (no API authentication required)
      const response = await fetch(`${API_BASE_URL}/api/facebook-ads/search-browser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keywords: searchKeywords.trim(),
          region: region,
          adType: adType,
          limit: 25
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSearchResults(data.ads || []);
        if (data.ads.length === 0) {
          setSearchError('No ads found. Try different keywords.');
        }
      } else {
        setSearchError(data.error || data.details || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching ads:', error);
      setSearchError('Failed to search ads: ' + error.message);
    } finally {
      setSearching(false);
    }
  };

  const handleExtract = async (ad) => {
    setExtracting(prev => ({ ...prev, [ad.id]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/facebook-ads/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adId: ad.id,
          adCopy: ad.adCreativeBody || ad.adCreativeLinkDescription || '',
          advertiserName: ad.pageName || '',
          landingPageUrl: null,
          adImageUrl: ad.adSnapshotUrl || null,
          sourceUrl: ad.sourceUrl || ad.pageUrl || ad.adSnapshotUrl || ''
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show extracted data and allow saving
        const extractedData = data.data;
        await handleSave(extractedData);
      } else {
        alert('Failed to extract data: ' + (data.error || data.details));
      }
    } catch (error) {
      console.error('Error extracting data:', error);
      alert('Failed to extract data: ' + error.message);
    } finally {
      setExtracting(prev => ({ ...prev, [ad.id]: false }));
    }
  };

  const handleSave = async (businessData) => {
    const leadId = businessData.id || `${Date.now()}-${businessData.businessName}`;
    setSaving(prev => ({ ...prev, [leadId]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/facebook-ads/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessData: businessData
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Reload leads
        await loadLeads();
      } else {
        alert('Failed to save lead: ' + (data.error || data.details));
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Failed to save lead: ' + error.message);
    } finally {
      setSaving(prev => ({ ...prev, [leadId]: false }));
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/facebook-ads/leads/${leadId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Reload leads
        await loadLeads();
      } else {
        alert('Failed to delete lead: ' + (data.error || data.details));
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead: ' + error.message);
    }
  };

  const handleExport = () => {
    if (leads.length === 0) {
      alert('No leads to export');
      return;
    }
    
    // Convert to CSV
    const headers = ['Business Name', 'WhatsApp', 'Phone', 'Website', 'Email', 'Ad URL', 'Advertiser', 'Extracted At'];
    const rows = leads.map(lead => [
      lead.businessName || '',
      lead.whatsappNumber || '',
      lead.phoneNumber || '',
      lead.website || '',
      lead.email || '',
      lead.sourceUrl || lead.landingPageUrl || lead.adImageUrl || '',
      lead.advertiserName || '',
      lead.extractedAt || lead.createdAt || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `facebook-leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="facebook-leads-tab">
      <div className="facebook-leads-header">
        <h2>Facebook Ad Library Leads</h2>
        <div className="facebook-leads-header-actions">
          <button 
            className="export-button"
            onClick={handleExport}
            disabled={leads.length === 0}
          >
            📥 Export CSV
          </button>
          <button 
            className="refresh-button"
            onClick={loadLeads}
            disabled={loadingLeads}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="facebook-leads-content">
        {/* Search Section */}
        <div className="facebook-leads-search-section">
          <h3>Search Facebook Ads</h3>
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-form-row">
              <div className="search-form-group">
                <label htmlFor="keywords">Keywords</label>
                <input
                  type="text"
                  id="keywords"
                  value={searchKeywords}
                  onChange={(e) => setSearchKeywords(e.target.value)}
                  placeholder="Enter search keywords..."
                  required
                />
              </div>
              
              <div className="search-form-group">
                <label htmlFor="region">Region</label>
                <select
                  id="region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                >
                  <option value="IL">Israel</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
              
              <div className="search-form-group">
                <label htmlFor="adType">Ad Type</label>
                <select
                  id="adType"
                  value={adType}
                  onChange={(e) => setAdType(e.target.value)}
                >
                  <option value="ALL">All</option>
                  <option value="POLITICAL_AND_ISSUE_ADS">Political</option>
                  <option value="HOUSING_ADS">Housing</option>
                  <option value="EMPLOYMENT_ADS">Employment</option>
                  <option value="CREDIT_ADS">Credit</option>
                </select>
              </div>
              
              <button 
                type="submit" 
                className="search-button"
                disabled={searching}
              >
                {searching ? '🔍 Searching...' : '🔍 Search'}
              </button>
            </div>
          </form>
          
          {searchError && (
            <div className="error-message">{searchError}</div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="facebook-leads-results-section">
            <h3>Search Results ({searchResults.length})</h3>
            <div className="search-results-list">
              {searchResults.map((ad) => (
                <div key={ad.id} className="search-result-item">
                  <div className="search-result-header">
                    <h4>{ad.pageName || 'Unknown Advertiser'}</h4>
                    <button
                      className="extract-button"
                      onClick={() => handleExtract(ad)}
                      disabled={extracting[ad.id]}
                    >
                      {extracting[ad.id] ? '⏳ Extracting...' : '📋 Extract & Save'}
                    </button>
                  </div>
                  
                  <div className="search-result-content">
                    {/* Contact Info - Always show Address, show WhatsApp/Phone if available */}
                    <div className="search-result-contact">
                      {ad.whatsappNumber && (
                        <div className="contact-item">
                          <span className="contact-label">WhatsApp:</span>
                          <a 
                            href={`https://wa.me/${ad.whatsappNumber.replace(/[^0-9+]/g, '')}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="contact-link whatsapp-link"
                          >
                            {ad.whatsappNumber}
                          </a>
                        </div>
                      )}
                      {ad.phoneNumber && (
                        <div className="contact-item">
                          <span className="contact-label">Phone:</span>
                          <a 
                            href={`tel:${ad.phoneNumber}`}
                            className="contact-link phone-link"
                          >
                            {ad.phoneNumber}
                          </a>
                        </div>
                      )}
                      <div className="contact-item">
                        <span className="contact-label">Ad URL:</span>
                        <span className={ad.sourceUrl || ad.pageUrl || ad.adSnapshotUrl ? "contact-value" : "contact-value no-address"}>
                          {ad.sourceUrl ? (
                            <a href={ad.sourceUrl} target="_blank" rel="noopener noreferrer" className="contact-link">
                              {ad.sourceUrl}
                            </a>
                          ) : ad.pageUrl ? (
                            <a href={ad.pageUrl} target="_blank" rel="noopener noreferrer" className="contact-link">
                              {ad.pageUrl}
                            </a>
                          ) : ad.adSnapshotUrl ? (
                            <a href={ad.adSnapshotUrl} target="_blank" rel="noopener noreferrer" className="contact-link">
                              {ad.adSnapshotUrl}
                            </a>
                          ) : (
                            'אין כתובת'
                          )}
                        </span>
                      </div>
                    </div>
                    
                    {/* Ad Image - Always show */}
                    <div className="search-result-image-preview">
                      {ad.adSnapshotUrl ? (
                        (() => {
                          const snapshotUrl = ad.adSnapshotUrl;
                          
                          // Check if it's an image URL
                          if (snapshotUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || snapshotUrl.startsWith('data:image') || snapshotUrl.includes('scontent') || snapshotUrl.includes('fbcdn')) {
                            // It's an image - show it directly
                            return (
                              <img 
                                src={snapshotUrl} 
                                alt="Ad snapshot" 
                                className="ad-snapshot-preview"
                                onClick={() => window.open(snapshotUrl, '_blank')}
                                title="Click to view full size"
                                onError={(e) => {
                                  // If image fails to load, hide it
                                  e.target.style.display = 'none';
                                }}
                              />
                            );
                          }
                          
                          // If it's a Facebook Ad Library URL, we can't show image directly
                          // But we'll show a placeholder with link
                          if (snapshotUrl.includes('/ads/library/') || snapshotUrl.includes('facebook.com/ads/library')) {
                            return (
                              <div className="ad-placeholder">
                                <div className="ad-placeholder-icon">📸</div>
                                <a 
                                  href={snapshotUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="view-ad-library-link"
                                >
                                  View Ad in Ad Library
                                </a>
                              </div>
                            );
                          }
                          
                          // Try to load as image anyway (might be a redirect)
                          return (
                            <img 
                              src={snapshotUrl} 
                              alt="Ad snapshot" 
                              className="ad-snapshot-preview"
                              onClick={() => window.open(snapshotUrl, '_blank')}
                              title="Click to view full size"
                              onError={(e) => {
                                // If image fails, show placeholder
                                e.target.style.display = 'none';
                                const placeholder = document.createElement('div');
                                placeholder.className = 'ad-placeholder';
                                placeholder.innerHTML = `
                                  <div class="ad-placeholder-icon">🔗</div>
                                  <a href="${snapshotUrl}" target="_blank" rel="noopener noreferrer" class="view-ad-snapshot-link">View Ad Snapshot</a>
                                `;
                                e.target.parentNode.appendChild(placeholder);
                              }}
                            />
                          );
                        })()
                      ) : (
                        <div className="ad-placeholder">
                          <div className="ad-placeholder-icon">📸</div>
                          <span className="no-image-text">אין תצלום מסך זמין</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Ad Text */}
                    {ad.adCreativeBody && (
                      <div className="search-result-body">
                        <p>{ad.adCreativeBody.substring(0, 200)}{ad.adCreativeBody.length > 200 ? '...' : ''}</p>
                      </div>
                    )}
                    
                    {/* Additional Links */}
                    <div className="search-result-links">
                      {ad.adSnapshotUrl && (
                        <>
                          {(() => {
                            const snapshotUrl = ad.adSnapshotUrl;
                            
                            // If it's an image, show link to view full
                            if (snapshotUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || snapshotUrl.startsWith('data:image') || snapshotUrl.includes('scontent') || snapshotUrl.includes('fbcdn')) {
                              return (
                                <a 
                                  href={snapshotUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="view-full-image-link"
                                >
                                  📷 View Full Image
                                </a>
                              );
                            }
                            
                            // If it's an Ad Library link
                            if (snapshotUrl.includes('/ads/library/') || snapshotUrl.includes('facebook.com/ads/library')) {
                              return (
                                <a 
                                  href={snapshotUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="view-ad-library-link"
                                >
                                  📚 View in Ad Library
                                </a>
                              );
                            }
                            
                            return null;
                          })()}
                        </>
                      )}
                      
                      {/* Try to build Ad Library link if we have ad ID from API */}
                      {ad.id && !ad.id.startsWith('browser-ad-') && ad.adSnapshotUrl && !ad.adSnapshotUrl.includes('/ads/library/') && (
                        <a 
                          href={`https://www.facebook.com/ads/library/?id=${ad.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-ad-library-link"
                        >
                          📚 View in Ad Library
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Saved Leads Section */}
        <div className="facebook-leads-saved-section">
          <h3>Saved Leads ({leads.length})</h3>
          
          {loadingLeads ? (
            <div className="loading-message">Loading leads...</div>
          ) : leadsError ? (
            <div className="error-message">{leadsError}</div>
          ) : leads.length === 0 ? (
            <div className="empty-message">No leads saved yet. Search and extract leads to get started.</div>
          ) : (
            <div className="leads-list">
              {leads.map((lead) => (
                <div key={lead.id} className="lead-item">
                  <div className="lead-header">
                    <h4>{lead.businessName || 'Unknown Business'}</h4>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(lead.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                  
                  <div className="lead-details">
                    <div className="lead-detail-row">
                      <span className="lead-label">Advertiser:</span>
                      <span className="lead-value">{lead.advertiserName || 'N/A'}</span>
                    </div>
                    
                    {lead.whatsappNumber && (
                      <div className="lead-detail-row">
                        <span className="lead-label">WhatsApp:</span>
                        <span className="lead-value">
                          <a href={`https://wa.me/${lead.whatsappNumber.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                            {lead.whatsappNumber}
                          </a>
                        </span>
                      </div>
                    )}
                    
                    {lead.phoneNumber && (
                      <div className="lead-detail-row">
                        <span className="lead-label">Phone:</span>
                        <span className="lead-value">
                          <a href={`tel:${lead.phoneNumber}`}>{lead.phoneNumber}</a>
                        </span>
                      </div>
                    )}
                    
                    {lead.website && (
                      <div className="lead-detail-row">
                        <span className="lead-label">Website:</span>
                        <span className="lead-value">
                          <a href={lead.website} target="_blank" rel="noopener noreferrer">
                            {lead.website}
                          </a>
                        </span>
                      </div>
                    )}
                    
                    {lead.email && (
                      <div className="lead-detail-row">
                        <span className="lead-label">Email:</span>
                        <span className="lead-value">
                          <a href={`mailto:${lead.email}`}>{lead.email}</a>
                        </span>
                      </div>
                    )}
                    
                    <div className="lead-detail-row">
                      <span className="lead-label">Ad URL:</span>
                      <span className={lead.sourceUrl || lead.landingPageUrl || lead.adImageUrl ? "lead-value" : "lead-value no-address"}>
                        {lead.sourceUrl ? (
                          <a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer">
                            {lead.sourceUrl}
                          </a>
                        ) : lead.landingPageUrl ? (
                          <a href={lead.landingPageUrl} target="_blank" rel="noopener noreferrer">
                            {lead.landingPageUrl}
                          </a>
                        ) : lead.adImageUrl ? (
                          <a href={lead.adImageUrl} target="_blank" rel="noopener noreferrer">
                            {lead.adImageUrl}
                          </a>
                        ) : (
                          'אין כתובת'
                        )}
                      </span>
                    </div>
                    
                    {/* Ad Image in Saved Leads */}
                    {lead.adImageUrl && (
                      <div className="lead-detail-row">
                        <span className="lead-label">Ad Screenshot:</span>
                        <div className="lead-value">
                          <img 
                            src={lead.adImageUrl} 
                            alt="Ad screenshot" 
                            className="lead-ad-image"
                            onClick={() => window.open(lead.adImageUrl, '_blank')}
                            title="Click to view full size"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {lead.adCopy && (
                      <div className="lead-detail-row">
                        <span className="lead-label">Ad Copy:</span>
                        <span className="lead-value ad-copy">{lead.adCopy.substring(0, 150)}{lead.adCopy.length > 150 ? '...' : ''}</span>
                      </div>
                    )}
                    
                    <div className="lead-detail-row">
                      <span className="lead-label">Extracted:</span>
                      <span className="lead-value">
                        {new Date(lead.extractedAt || lead.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FacebookLeadsTab;

